import { Chip, ChipProps, Typography, useTheme } from '@mui/material';
import { DoorBatteryWarningLevel } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import BatteryAndroid0Outlined from 'v2/assets/vector/lockIcons/BatteryAndroid0Outlined';
import BatteryAndroidFrame1Outlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrame1Outlined';
import BatteryAndroidFrame3Outlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrame3Outlined';
import BatteryAndroidFrameBoltOutlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrameBoltOutlined';
import BatteryAndroidFrameFullOutlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrameFullOutlined';
import BatteryAndroidFrameQuestionOutlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrameQuestionOutlined';

interface Props {
  level: DoorBatteryWarningLevel;
  sx?: ChipProps['sx'];
}

const BatteryStatusChip: React.FC<Props> = ({ level, sx }) => {
  const { translation } = useTranslation();
  const theme = useTheme();

  const config = (() => {
    switch (level) {
      case DoorBatteryWarningLevel.BatteryDefault:
        return { icon: <BatteryAndroidFrameFullOutlined />, color: 'success' as const, label: translation.full };
      case DoorBatteryWarningLevel.BatteryLow:
        return { icon: <BatteryAndroidFrame3Outlined />, color: 'warning' as const, label: translation.low };
      case DoorBatteryWarningLevel.BatteryCritical:
        return { icon: <BatteryAndroidFrame1Outlined />, color: 'error' as const, label: translation.critical };
      case DoorBatteryWarningLevel.BatteryEmpty:
        return { icon: <BatteryAndroid0Outlined />, color: 'error' as const, label: translation.empty };
      case DoorBatteryWarningLevel.Powered:
        return { icon: <BatteryAndroidFrameBoltOutlined />, color: 'success' as const, label: translation.powered };
      case DoorBatteryWarningLevel.BatteryUnknown:
      default:
        return {
          icon: <BatteryAndroidFrameQuestionOutlined sx={{ color: theme.palette.secondary.light }} />,
          color: 'secondary' as const,
          label: translation.unknown
        };
    }
  })();

  if (!config) return null;

  return (
    <Chip
      variant="filled"
      size="small"
      color={config.color}
      icon={config.icon}
      label={<Typography variant="caption">{config.label}</Typography>}
      sx={sx}
    />
  );
};

export default BatteryStatusChip;
